// /components/PuzzleHub.js
import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Fade,
  CssBaseline,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  SportsEsports,
  Extension,
  Psychology,
  Palette,
  Calculate,
  Timeline,
  LightMode,
  DarkMode,
  InfoOutlined,
  
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FloatingHomeReturn from "./FloatingHomeReturn";

const PuzzleHub = () => {
  const [themeMode, setThemeMode] = useState("light");
  const [openTip, setOpenTip] = useState(null);
  const navigate = useNavigate();

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#64b5f6" },
      secondary: { main: "#ff9800" },
      background: {
        default: themeMode === "light" ? "#f5f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#ffffff" : "#161b22",
      },
    },
    typography: { fontFamily: "Inter, sans-serif" },
    shape: { borderRadius: 16 },
  });

  const games = [
    {
      title: "Snake Game",
      path: "/snake-game",
      icon: <SportsEsports fontSize="large" />,
      color: "#0a84ff",
      desc: "Classic snake with a sleek look.",
      tip: "🪱 Eat red food to grow longer. Avoid biting yourself!",
    },
    {
      title: "Sliding Puzzle",
      path: "/sliding-puzzle",
      icon: <Extension fontSize="large" />,
      color: "#ff9800",
      desc: "Arrange the tiles to restore order.",
      tip: "🧩 Try solving corners first, then edges for efficiency.",
    },
    {
      title: "Memory Match",
      path: "/memory-match",
      icon: <Psychology fontSize="large" />,
      color: "#8bc34a",
      desc: "Flip cards to find matching emoji pairs.",
      tip: "🧠 Focus on patterns; recall previous card positions.",
    },
    {
      title: "Color Match",
      path: "/color-match",
      icon: <Palette fontSize="large" />,
      color: "#e91e63",
      desc: "Tap the correct color before time runs out.",
      tip: "🎨 Focus on text vs. color — it’s trickier than it looks!",
    },
    {
      title: "Math Reflex",
      path: "/math-reflex",
      icon: <Calculate fontSize="large" />,
      color: "#4caf50",
      desc: "Solve math problems quickly to score high.",
      tip: "🔢 Think fast — accuracy is key to reaching higher scores.",
    },
    {
      title: "Sequence Memory",
      path: "/seq-memory",
      icon: <Timeline fontSize="large" />,
      color: "#9c27b0",
      desc: "Repeat the flashing pattern to advance.",
      tip: "⚡ Memorize the rhythm and sequence of lights carefully.",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Fade in timeout={600}>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background:
              themeMode === "light"
                ? "linear-gradient(135deg, #f0f4ff, #c3d9ff)"
                : "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
            transition: "all 0.4s ease",
            py: 6,
            px: 2,
          }}
        >
          <FloatingHomeReturn/>
          {/* Theme Toggle */}
          <IconButton
            onClick={() =>
              setThemeMode(themeMode === "light" ? "dark" : "light")
            }
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "primary.main",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "50%",
              boxShadow: 2,
            }}
          >
            {themeMode === "light" ? <DarkMode /> : <LightMode />}
          </IconButton>

          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "primary.main",
              textShadow: "0 0 10px rgba(25,118,210,0.3)",
            }}
          >
            🧠 PuzzleHub
          </Typography>

          <Grid container spacing={3} justifyContent="center" maxWidth="md">
            {games.map((game, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    borderRadius: 4,
                    background: theme.palette.mode === "light"
                      ? "rgba(255,255,255,0.85)"
                      : "rgba(30,30,30,0.6)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(game.path)}
                    sx={{ p: 2 }}
                  >
                    <CardContent sx={{ textAlign: "center", py: 3 }}>
                      <Box
                        sx={{
                          color: game.color,
                          mb: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {game.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {game.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        {game.desc}
                      </Typography>
                      <Tooltip title="View Tip" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenTip(game);
                          }}
                          sx={{ color: game.color }}
                        >
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Tips Dialog */}
          <Dialog open={!!openTip} onClose={() => setOpenTip(null)}>
            <DialogContent
              sx={{
                textAlign: "center",
                p: 3,
                background: theme.palette.background.paper,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold" color="primary">
                {openTip?.title} Tip
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {openTip?.tip}
              </Typography>
            </DialogContent>
          </Dialog>

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ mt: 6 }}
          >
            © {new Date().getFullYear()} PuzzleHub by Zolang Nexora
          </Typography>
        </Box>
      </Fade>
    </ThemeProvider>
  );
};

export default PuzzleHub;
